/* The writes the reconciliation screens gained with the redesign, and the two reads nothing had.
 *
 * Kept beside `reconciliation.ts` rather than inside it because that file's own docstring states the
 * boundary it was written to — "reads plus one write, and the write is the only one the API has" —
 * and argues at length why authoring and publishing were deliberately left out. They are in now,
 * with the argument answered rather than deleted: the design makes the key-column pairing the whole
 * subject of step 1 (which is where `keys_disjoint` is prevented), and makes a publish a diff
 * against the version in force behind a statement that it is permanent.
 *
 * No `X-Lumiq-Tenant` header here or anywhere in `src/`: hard rule 1, the edge sets the tenant from
 * a validated token claim.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { isAuditEntry } from '@/shared/api/generated/audit';
import type { AuditEntry } from '@/shared/api/generated/audit';
import {
  isBreakPolicy,
  isDefinition,
  isNoRunSchedule,
  isRunSchedule,
  isToleranceVersion,
} from '@/shared/api/generated/reconciliation';
import type {
  BreakPolicy,
  Cadence,
  Definition,
  DefinitionWrite,
  FieldTolerance,
  NoRunSchedule,
  RunSchedule,
  ToleranceVersion,
} from '@/shared/api/generated/reconciliation';
import {
  forgetIdempotencyKey,
  getOne,
  getPage,
  idempotencyKeyFor,
  post,
  put,
} from '@/shared/api/http';

import { DEFINITIONS_URL } from './reconciliation';

function definitionUrl(definitionId: string): string {
  return `${DEFINITIONS_URL}/${encodeURIComponent(definitionId)}`;
}

/* ------------------------------------------------------------------ authoring a definition */

/** Create a definition and its first tolerance version, which the API does as one act.
 *
 * **Keyed on the name rather than minted per click**, and the name is the right subject because it
 * is what the server deduplicates on: unique per tenant, compared case- and space-insensitively. So
 * a double-submitted form is a replay of the first request rather than a 422 telling somebody their
 * own definition already exists.
 *
 * Forgotten on success so the next deliberate creation is a new request. Without that, correcting a
 * name and submitting again would replay the first attempt.
 */
export function useCreateDefinition(): UseMutationResult<
  Definition,
  Error,
  DefinitionWrite
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: DefinitionWrite) =>
      post(
        DEFINITIONS_URL,
        idempotencyKeyFor('reconciliation-definition', body.name.trim().toLowerCase()),
        isDefinition,
        'reconciliation definition',
        body,
      ),
    onSuccess: (_definition, body) => {
      forgetIdempotencyKey('reconciliation-definition', body.name.trim().toLowerCase());
      void client.invalidateQueries({ queryKey: ['reconciliation', 'definitions'] });
    },
  });
}

/* ------------------------------------------------------------------ publishing tolerances */

/** Publish the next tolerance version. Appends; never edits.
 *
 * **Keyed on the definition plus the version being published**, not on the definition alone. A
 * publish is the one write here somebody may legitimately repeat within a session — retune, publish
 * v5, retune again, publish v6 — and a definition-only key would make the second publish a replay of
 * the first, silently discarding the second set of bounds while reporting success.
 */
export function usePublishTolerances(
  definitionId: string,
): UseMutationResult<
  ToleranceVersion,
  Error,
  { readonly nextVersion: number; readonly tolerances: readonly FieldTolerance[] }
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (request) =>
      post(
        `${definitionUrl(definitionId)}/tolerances`,
        idempotencyKeyFor(
          'reconciliation-tolerances',
          `${definitionId}:${String(request.nextVersion)}`,
        ),
        isToleranceVersion,
        'tolerance version',
        { tolerances: request.tolerances },
      ),
    onSuccess: (_version, request) => {
      forgetIdempotencyKey(
        'reconciliation-tolerances',
        `${definitionId}:${String(request.nextVersion)}`,
      );
      void client.invalidateQueries({
        queryKey: ['reconciliation', 'definition', definitionId],
      });
    },
  });
}

/* ------------------------------------------------------------------ the break policy */

/** What a disagreement on this definition is worth telling somebody.
 *
 * **Always answers**, so there is no 404 branch: a tenant who has declared nothing gets the platform
 * default with `declared: false`, which the screen renders as the platform's choice rather than
 * somebody's. Presenting a default as a decision is how a tenant comes to believe a threshold was
 * set that nobody set.
 */
export function useBreakPolicy(definitionId: string): UseQueryResult<BreakPolicy> {
  return useQuery({
    queryKey: ['reconciliation', 'break-policy', definitionId],
    queryFn: () =>
      getOne(
        `${definitionUrl(definitionId)}/break-policy`,
        isBreakPolicy,
        'break policy',
      ),
    retry: false,
  });
}

export interface BreakPolicyWrite {
  readonly enabled: boolean;
  readonly severity: BreakPolicy['severity'];
  readonly min_breaks: number;
}

/** Declare or retune the policy. A PUT, so no `Idempotency-Key`: the natural key is in the URL and a
 * retry is the same declaration rather than a second one. */
export function useSaveBreakPolicy(
  definitionId: string,
): UseMutationResult<BreakPolicy, Error, BreakPolicyWrite> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: BreakPolicyWrite) =>
      put(
        `${definitionUrl(definitionId)}/break-policy`,
        isBreakPolicy,
        'break policy',
        body,
      ),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ['reconciliation', 'break-policy', definitionId],
      });
    },
  });
}

/* ------------------------------------------------------------------ the run schedule */

/** The schedule, or a stated absence.
 *
 * **Two shapes on one route, and the union is the design rather than an inconvenience.** A definition
 * nobody has scheduled answers `configured: false` with a sentence, not a 404 — because a 404 would
 * make "nobody has considered a schedule" indistinguishable from "no such definition". The guard
 * accepts either and the screen branches on `configured`.
 */
export type ScheduleReading = RunSchedule | NoRunSchedule;

function isScheduleReading(value: unknown): value is ScheduleReading {
  return isRunSchedule(value) || isNoRunSchedule(value);
}

/** Exported as a query *object* for `definitionQuery`'s reason: the board reads a bounded number of
 * schedules through `useQueries`, and the count of definitions is data where a hook count cannot be. */
export function runScheduleQuery(definitionId: string): {
  readonly queryKey: readonly unknown[];
  readonly queryFn: () => Promise<ScheduleReading>;
  readonly retry: false;
} {
  return {
    queryKey: ['reconciliation', 'run-schedule', definitionId],
    queryFn: () =>
      getOne(
        `${definitionUrl(definitionId)}/run-schedule`,
        isScheduleReading,
        'run schedule',
      ),
    retry: false,
  };
}

export function useRunSchedule(definitionId: string): UseQueryResult<ScheduleReading> {
  return useQuery(runScheduleQuery(definitionId));
}

export interface RunScheduleWrite {
  readonly enabled: boolean;
  readonly cadence: Cadence;
  readonly local_time: string;
  readonly timezone: string;
  readonly weekdays: readonly number[];
}

/** Configure the cadence. **Enabling is part of configuring**, so a grant that can start unattended
 * joining of two of a customer's largest tables can also stop it. A PUT for `useSaveBreakPolicy`'s
 * reason. */
export function useSaveRunSchedule(
  definitionId: string,
): UseMutationResult<RunSchedule, Error, RunScheduleWrite> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: RunScheduleWrite) =>
      put(
        `${definitionUrl(definitionId)}/run-schedule`,
        isRunSchedule,
        'run schedule',
        body,
      ),
    onSuccess: () => {
      void client.invalidateQueries({
        queryKey: ['reconciliation', 'run-schedule', definitionId],
      });
    },
  });
}

/* ------------------------------------------------------------------ the definition's history */

/** The aggregate type the audit chain records a reconciliation definition under. */
export const DEFINITION_AGGREGATE = 'ReconciliationDefinition';

/** How many chain entries the history panel reads. A definition accumulates one entry per authoring
 * act and one per scheduled occurrence, so a year of a daily schedule is a few hundred; this is the
 * recent window a reader scans rather than an archive. */
const HISTORY_LIMIT = 50;

/** Everything done *to* this definition, from the tamper-evident chain.
 *
 * **Queried by `aggregate_id` alone, deliberately without `aggregate_type`.** Three aggregate types
 * write against a definition's own id — the definition itself, its break policy, and its run
 * schedule — so filtering by type would return a third of the history while looking complete. Runs
 * are a *different* aggregate with their own ids, so they are absent here by construction, which is
 * right: they live in the ledger above and merging the two would be an invented timeline.
 */
export function useDefinitionHistory(
  definitionId: string,
  options: { readonly enabled: boolean },
): UseQueryResult<readonly AuditEntry[]> {
  return useQuery({
    queryKey: ['reconciliation', 'history', definitionId],
    queryFn: async () => {
      const params = new URLSearchParams({
        aggregate_id: definitionId,
        limit: String(HISTORY_LIMIT),
      });
      return (
        await getPage(
          `/v1/audit/entries?${params.toString()}`,
          isAuditEntry,
          'audit entry',
        )
      ).items;
    },
    retry: false,
    enabled: options.enabled,
  });
}
